import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Bell, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useTran } from "@/hooks";
import { PATHS } from "@/lib/router";
import { showToast } from "@/lib/utils/toast";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/features/auth";
import { useNotificationsSocket, useNotificationsStore } from "../hooks";
import { formatRelativeTime } from "../utils/formatRelativeTime";

const MAX_ITEMS = 8;

const NotificationsBell = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { user, accessToken } = useAuthStore();
  const store = useNotificationsStore();
  const userId = user?.userId || user?.id || user?._id || null;

  useNotificationsSocket();

  useEffect(() => {
    store.setUserContext(userId);
    if (userId && accessToken) {
      store.loadNotifications();
    }
  }, [accessToken, store, userId]);

  const items = store.items.slice(0, MAX_ITEMS);
  const hasItems = items.length > 0;

  const handleMarkAllRead = async () => {
    const meta = await store.markAllRead();
    const count = meta?.updated ?? store.items.length;
    if (meta) {
      showToast.success(t("notifications.markAllReadSuccess", { count }));
    } else {
      showToast.error(t("notifications.markAllReadError"));
    }
  };

  const handleClearAll = async () => {
    const snapshot = store.items.length;
    const markMeta = await store.markAllRead();
    const clearMeta = await store.clearAll();
    const count = clearMeta?.cleared ?? markMeta?.updated ?? snapshot;
    if (clearMeta) {
      showToast.success(t("notifications.clearAllSuccess", { count }));
    } else {
      showToast.error(t("notifications.clearAllError"));
    }
  };

  const handleItemClick = (note) => {
    if (!note) return;
    navigate(PATHS.NOTIFICATIONS, {
      state: {
        focusNotificationId: note.id || null,
        focusNotification: note,
      },
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="relative"
          aria-label={t("notifications.title")}
        >
          <Bell className="h-4 w-4" />
          {store.unread > 0 ? (
            <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-[1.1rem] items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold text-white">
              {store.unread > 9 ? "9+" : store.unread}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2 text-sm font-semibold">
          <span>{t("notifications.title")}</span>
          <div className="flex items-center gap-2 text-xs font-normal">
            <button
              type="button"
              className="text-primary hover:underline disabled:pointer-events-none disabled:opacity-50"
              disabled={!store.hasItems}
              onClick={handleMarkAllRead}
            >
              {t("notifications.markAllRead")}
            </button>
            <span className="text-muted-foreground">|</span>
            <button
              type="button"
              className="text-destructive hover:underline disabled:pointer-events-none disabled:opacity-50"
              disabled={!store.hasItems}
              onClick={handleClearAll}
            >
              {t("notifications.clearAll")}
            </button>
          </div>
        </div>
        {hasItems ? (
          <div className="max-h-96 overflow-y-auto">
            {items.map((note) => {
              const isUnread = !note.readAt;
              return (
                <DropdownMenuItem
                  key={note.id || note.createdAt}
                  className="flex items-start gap-2 border-b border-border px-3 py-3 last:border-b-0"
                  onSelect={(event) => {
                    event.preventDefault();
                    handleItemClick(note);
                  }}
                >
                  <div className="mt-1 flex h-2.5 w-2.5 flex-none items-center justify-center">
                    {isUnread ? (
                      <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        "text-sm",
                        isUnread
                          ? "font-semibold text-foreground"
                          : "text-muted-foreground",
                      )}
                    >
                      {note.message || t("notifications.fallback")}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {formatRelativeTime(note.createdAt, {
                        fallback: t("notifications.justNow"),
                      })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={(event) => {
                      event.preventDefault();
                      handleItemClick(note);
                    }}
                  >
                    {t("notifications.view")}
                  </Button>
                </DropdownMenuItem>
              );
            })}
            {store.items.length > MAX_ITEMS ? (
              <DropdownMenuItem className="justify-center text-xs text-muted-foreground">
                {t("notifications.latest", { count: MAX_ITEMS })}
              </DropdownMenuItem>
            ) : null}
          </div>
        ) : (
          <div className="p-4 text-sm text-muted-foreground">
            {t("notifications.empty")}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="justify-center text-sm"
          onSelect={(event) => {
            event.preventDefault();
            navigate(PATHS.NOTIFICATIONS);
          }}
        >
          {t("notifications.viewAll")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export default NotificationsBell;
